import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import axios from 'axios';
import DOMPurify from 'dompurify';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/articles/${slug}`);
        setArticle(response.data);
      } catch (error) {
        console.error('Failed to load content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <p className="text-[#8B7D6B]">Loading article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex flex-col items-center justify-center">
        <p className="text-[#8B7D6B] mb-4">Article not found</p>
        <Link to="/tips-inspiration">
          <Button className="bg-[#DE92A9] hover:bg-[#C9758F]">
            Back to Tips & Inspiration
          </Button>
        </Link>
      </div>
    );
  }

  // Parse the content into sections
  const renderContent = (content) => {
    if (!content) return null;
    
    // Check if content contains HTML
    if (content.includes('<img') || content.includes('<a href')) {
      return (
        <div 
          className="article-content prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatContentWithHTML(content)) }}
        />
      );
    }
    
    const sections = content.split('\n\n').filter(s => s.trim());
    
    return sections.map((section, index) => {
      // Check if it's a heading (all caps)
      const isHeading = section === section.toUpperCase() && section.length < 100;
      
      if (isHeading) {
        return (
          <h3 
            key={`${index}-${section.slice(0, 24)}`}
            className="text-xl md:text-2xl text-[#2C2C2C] mt-10 mb-4 tracking-tight font-medium"
          >
            {section}
          </h3>
        );
      }
      
      return (
        <p key={`${index}-${section.slice(0, 24)}`} className="text-[#6B4E37] text-lg leading-relaxed mb-6">
          {section}
        </p>
      );
    });
  };

  // Format content with HTML elements
  const formatContentWithHTML = (content) => {
    // Split by double newlines to get paragraphs
    const sections = content.split('\n\n').filter(s => s.trim());
    
    return sections.map(section => {
      // If it contains an img tag, return as is
      if (section.includes('<img')) {
        return section;
      }
      
      // Check if it's a heading (all caps, no HTML)
      const isHeading = section === section.toUpperCase() && section.length < 100 && !section.includes('<');
      
      if (isHeading) {
        return `<h3 class="text-xl md:text-2xl text-[#2C2C2C] mt-10 mb-4 tracking-tight font-medium">${section}</h3>`;
      }
      
      // Check if it's a list
      if (section.startsWith('-')) {
        const items = section.split('\n').filter(s => s.trim());
        const listItems = items.map(item => `<li class="text-[#6B4E37] mb-2">${item.replace(/^-\s*/, '')}</li>`).join('');
        return `<ul class="list-disc list-inside mb-6 text-[#6B4E37]">${listItems}</ul>`;
      }
      
      // Regular paragraph with links
      return `<p class="text-[#6B4E37] text-lg leading-relaxed mb-6">${section}</p>`;
    }).join('');
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <SEO
        title={article.title}
        description={article.excerpt || article.title}
        image={article.image}
        type="article"
        keywords={article.keywords || `malta wedding, ${article.category || 'wedding inspiration'}, bridal magazine, wedding planning malta`}
        publishedDate={article.created_at ? article.created_at.slice(0, 10) : article.date}
        author="I DO Editorial Team"
        breadcrumbs={[
          { name: 'Tips & Inspiration', url: '/tips-inspiration' },
          { name: article.title, url: `/tips-inspiration/${article.slug}` },
        ]}
      />
      {/* Hero Image */}
      <div className="relative h-[50vh] overflow-hidden">
        <img 
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        
        {/* Back Button */}
        <Link 
          to="/tips-inspiration"
          className="absolute top-6 left-6 flex items-center gap-2 text-white hover:text-[#DE92A9] transition-colors z-10"
        >
          <ArrowLeft size={20} />
          <span className="text-sm tracking-wider">Back to Articles</span>
        </Link>

        {/* Category Badge */}
        <Badge className="absolute top-6 right-6 bg-[#DE92A9] text-white border-none">
          {article.category}
        </Badge>
      </div>

      {/* Article Content */}
      <article className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title and Meta */}
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-4 text-sm text-[#8B7D6B] mb-4">
              <div className="flex items-center gap-1">
                <Calendar size={14} />
                <span>{article.date}</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-1">
                <Tag size={14} />
                <span>{article.category}</span>
              </div>
            </div>
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl text-[#2C2C2C] leading-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              {article.title}
            </h1>
          </div>

          {/* Excerpt */}
          <div className="border-l-4 border-[#DE92A9] pl-6 mb-10">
            <p className="text-lg text-[#6B4E37] italic leading-relaxed">
              {article.excerpt}
            </p>
          </div>

          {/* Full Content */}
          <div className="prose prose-lg max-w-none">
            {renderContent(article.content)}
          </div>
        </div>

        {/* Advertising Banner */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <div className="bg-gradient-to-r from-[#DE92A9] to-[#E8AABC] rounded-lg p-8 md:p-12 text-center">
            <p className="text-white/80 text-xs tracking-[0.3em] uppercase mb-3">ADVERTISEMENT</p>
            <h3 
              className="text-2xl md:text-3xl text-white mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Planning Your Dream Wedding?
            </h3>
            <p className="text-white/90 mb-6 max-w-xl mx-auto">
              Connect with Malta's finest wedding vendors and make your vision a reality.
            </p>
            <Link to="/partners">
              <Button className="bg-white text-[#6B4E37] hover:bg-[#FAF9F7] px-8 py-3">
                FIND WEDDING VENDORS
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Related Articles CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl text-[#2C2C2C] mb-6"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            More Wedding Inspiration
          </h2>
          <p className="text-[#6B4E37] mb-8">
            Explore more tips, trends, and advice for your wedding journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/tips-inspiration">
              <Button className="bg-[#DE92A9] hover:bg-[#C9758F] text-white px-8 py-4 text-sm tracking-wider">
                VIEW ALL ARTICLES
              </Button>
            </Link>
            <Link to="/wedding-stories">
              <Button variant="outline" className="border-2 border-[#DE92A9] text-[#6B4E37] hover:bg-[#DE92A9] hover:text-white px-8 py-4 text-sm tracking-wider">
                REAL WEDDING STORIES
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleDetailPage;
